import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import type { HelpBlock } from "@/data/helpArticles";

/**
 * Renders a help-article block. Inline `text` fields support a small
 * superset of plain text:
 *
 *   **bold**            → <strong>
 *   [label](href)       → <Link> for /paths, <a target="_blank"> for absolute URLs
 *
 * Kept deliberately tiny — no markdown lib in the bundle. If we ever need
 * tables, images, or code, switch to react-markdown.
 */
export function HelpRenderer({ blocks }: { blocks: HelpBlock[] }) {
  return (
    <div className="space-y-4 text-[15px] leading-relaxed text-fh-ink">
      {blocks.map((b, i) => {
        switch (b.type) {
          case "h":
            return (
              <h2 key={i} className="font-serif text-[19px] text-fh-ink mt-6 mb-1">
                {renderInline(b.text)}
              </h2>
            );
          case "p":
            return (
              <p key={i} className="m-0">
                {renderInline(b.text)}
              </p>
            );
          case "ul":
            return (
              <ul key={i} className="list-disc pl-6 space-y-1.5 marker:text-fh-gray">
                {b.items.map((item, j) => (
                  <li key={j}>{renderInline(item)}</li>
                ))}
              </ul>
            );
          case "ol":
            return (
              <ol key={i} className="list-decimal pl-6 space-y-1.5 marker:text-fh-gray">
                {b.items.map((item, j) => (
                  <li key={j}>{renderInline(item)}</li>
                ))}
              </ol>
            );
          case "note": {
            const styles: Record<"info" | "warning" | "success", string> = {
              info:    "bg-fh-sage/30 border-fh-green-light/50 text-fh-ink",
              warning: "bg-amber-50    border-amber-200          text-amber-900",
              success: "bg-emerald-50  border-emerald-200        text-emerald-900",
            };
            return (
              <div
                key={i}
                className={`rounded-[10px] border px-4 py-3 text-[14px] leading-relaxed ${styles[b.tone]}`}
              >
                {renderInline(b.text)}
              </div>
            );
          }
        }
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline text renderer — handles **bold** and [label](href).
// ---------------------------------------------------------------------------

/**
 * Tokenize an inline string into ReactNodes. Two passes:
 *   1. Split on [label](href) link patterns first
 *   2. Within each non-link segment, split on **bold** markers
 *
 * Order matters because a bold marker inside a link label gets preserved
 * (we re-recurse into the label text).
 */
export function renderInline(text: string): ReactNode {
  const out: ReactNode[] = [];
  let cursor = 0;
  const linkRe = /\[([^\]]+)\]\(([^)]+)\)/g;
  let m: RegExpExecArray | null;
  while ((m = linkRe.exec(text)) !== null) {
    if (m.index > cursor) {
      out.push(...splitBold(text.slice(cursor, m.index)));
    }
    // Capture groups 1 and 2 always present given the regex shape; assert
    // for the type checker (tsconfig has noUncheckedIndexedAccess on).
    const label = m[1] ?? "";
    const href = m[2] ?? "";
    out.push(renderLink(label, href, out.length));
    cursor = m.index + m[0].length;
  }
  if (cursor < text.length) {
    out.push(...splitBold(text.slice(cursor)));
  }
  return <>{out}</>;
}

function splitBold(s: string): ReactNode[] {
  const out: ReactNode[] = [];
  let cursor = 0;
  const boldRe = /\*\*([^*]+)\*\*/g;
  let m: RegExpExecArray | null;
  while ((m = boldRe.exec(s)) !== null) {
    if (m.index > cursor) out.push(s.slice(cursor, m.index));
    out.push(
      <strong key={`b-${m.index}`} className="font-semibold text-fh-ink">
        {m[1]}
      </strong>,
    );
    cursor = m.index + m[0].length;
  }
  if (cursor < s.length) out.push(s.slice(cursor));
  return out;
}

function renderLink(label: string, href: string, key: number): ReactNode {
  // Intra-portal routes go through react-router so we don't full-reload.
  if (href.startsWith("/")) {
    return (
      <Link
        key={`l-${key}`}
        to={href}
        className="text-fh-green-dark underline underline-offset-2 hover:text-fh-green font-medium"
      >
        {renderInline(label)}
      </Link>
    );
  }
  // External — open in new tab, never leak referrer.
  return (
    <a
      key={`l-${key}`}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-fh-green-dark underline underline-offset-2 hover:text-fh-green font-medium"
    >
      {renderInline(label)}
    </a>
  );
}
